import { useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Button, Caption1, Dropdown, Input, Option } from '@fluentui/react-components';
import { DismissRegular, SearchRegular } from '@fluentui/react-icons';
import CalendarDatePicker from '@shared/components/CalendarDatePicker';
import { formatCalendarDate, ymdToLocalDate } from '@shared/utils/date';
import {
  MAX_REGISTRATION_SEARCH_LENGTH,
  REGISTRATION_STATUS_OPTIONS,
  REGISTRATION_VALIDITY_OPTIONS,
} from '../constants';
import type { ERegistrationStatus, ERegistrationValidity, TRegistrationFilters } from '../types';

type TProps = {
  filters: TRegistrationFilters;
  onChange: (changes: Partial<TRegistrationFilters>) => void;
};

const SEARCH_DEBOUNCE_MS = 300;

const EMPTY_FILTERS: Partial<TRegistrationFilters> = {
  search: '',
  status: [],
  validity: undefined,
  validUntilFrom: '',
  validUntilTo: '',
};

const RegistrationsFilterBar = ({ filters, onChange }: TProps) => {
  const [searchInput, setSearchInput] = useState(filters.search);
  const validUntilFrom = useMemo(() => ymdToLocalDate(filters.validUntilFrom), [filters.validUntilFrom]);
  const validUntilTo = useMemo(() => ymdToLocalDate(filters.validUntilTo), [filters.validUntilTo]);
  const debouncedSearch = useDebouncedCallback((search: string) => onChange({ search }), SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    debouncedSearch.cancel();
    setSearchInput(filters.search);
  }, [debouncedSearch, filters.search]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const clearFilters = () => {
    debouncedSearch.cancel();
    setSearchInput('');
    onChange(EMPTY_FILTERS);
  };

  const statusLabels = REGISTRATION_STATUS_OPTIONS.filter(({ value }) => filters.status.includes(value)).map(
    ({ label }) => label
  );
  const hasFilters = Boolean(
    searchInput ||
      filters.search ||
      filters.status.length ||
      filters.validity ||
      filters.validUntilFrom ||
      filters.validUntilTo
  );

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Input
        contentBefore={<SearchRegular />}
        placeholder="Registration name or identifier"
        value={searchInput}
        maxLength={MAX_REGISTRATION_SEARCH_LENGTH}
        onChange={(_, data) => {
          setSearchInput(data.value);
          debouncedSearch(data.value);
        }}
        className="min-w-56 max-w-96 flex-1"
      />
      <Dropdown
        multiselect
        placeholder="All statuses"
        selectedOptions={filters.status}
        value={statusLabels.slice(0, 2).join(', ') + (statusLabels.length > 2 ? ', ...' : '')}
        onOptionSelect={(_, data) => onChange({ status: data.selectedOptions as ERegistrationStatus[] })}
        className="w-52"
      >
        {REGISTRATION_STATUS_OPTIONS.map(({ value, label }) => (
          <Option key={value} value={value} text={label}>
            {label}
          </Option>
        ))}
      </Dropdown>
      <Dropdown
        placeholder="Any validity"
        selectedOptions={[filters.validity ?? '']}
        value={REGISTRATION_VALIDITY_OPTIONS.find(({ value }) => value === filters.validity)?.label ?? ''}
        onOptionSelect={(_, data) =>
          onChange({ validity: (data.optionValue || undefined) as ERegistrationValidity | undefined })
        }
        className="w-56"
      >
        <Option value="" text="Any validity">
          Any validity
        </Option>
        {REGISTRATION_VALIDITY_OPTIONS.map(({ value, label }) => (
          <Option key={value} value={value} text={label}>
            {label}
          </Option>
        ))}
      </Dropdown>
      <div className="flex flex-wrap items-center gap-3">
        <Caption1 className="text-neutral-500">Expiry date</Caption1>
        <div className="flex items-center gap-2">
          <CalendarDatePicker
            aria-label="Expiry date from"
            placeholder="From"
            clearLabel="Clear expiry date from"
            clearable
            value={validUntilFrom}
            onSelectDate={(date) => onChange({ validUntilFrom: formatCalendarDate(date) })}
            className="w-44"
          />
          <Caption1 aria-hidden="true" className="text-neutral-500">
            -
          </Caption1>
          <CalendarDatePicker
            aria-label="Expiry date to"
            placeholder="To"
            clearLabel="Clear expiry date to"
            clearable
            value={validUntilTo}
            onSelectDate={(date) => onChange({ validUntilTo: formatCalendarDate(date) })}
            className="w-44"
          />
        </div>
      </div>
      {hasFilters && (
        <Button appearance="subtle" icon={<DismissRegular />} onClick={clearFilters}>
          Clear filters
        </Button>
      )}
    </div>
  );
};

export default RegistrationsFilterBar;
